import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Menu,
  NumberInput,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconChevronDown, IconPlus } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// Phase Accounting-11/12: سجل الشيكات. Opens on "due this week" — the
// UX-blueprint rule that registers open on what needs attention, not
// on "all". Status transitions post their GL entries server-side.

interface ChequeDto {
  chequeId: string;
  direction: number;
  chequeNumber: string;
  bankNameAr: string;
  drawerName: string;
  amount: number;
  currencyCode: string;
  issueDate: string;
  dueDate: string;
  status: number;
  notes: string | null;
}

interface ChequePageDto {
  total: number;
  rows: ChequeDto[];
}

const QK = ['cheques'] as const;

type ViewMode = 'due-week' | 'pending' | 'all';

export function ChequesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [view, setView] = useState<ViewMode>('due-week');
  const [direction, setDirection] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, pageSize: 50 };
    if (direction) p.direction = direction;
    if (view === 'due-week') {
      const today = new Date();
      const weekAhead = new Date(today.getTime() + 7 * 86_400_000);
      p.dueTo = toIsoDate(weekAhead);
      p.status = 1; // pending
    } else if (view === 'pending') {
      p.status = 1;
    }
    return p;
  }, [view, direction, page]);

  const { data, isLoading } = useQuery({
    queryKey: [...QK, params] as const,
    queryFn: async () =>
      (await apiClient.get<ChequePageDto>('/api/accounting/cheques', { params })).data,
  });

  const changeStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: number }) =>
      (await apiClient.post(`/api/accounting/cheques/${id}/status`, { newStatus: status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.cheques.title')}
        subtitle={t('accounting.cheques.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap">
          <Group>
            <SegmentedControl
              value={view}
              onChange={(v) => { setView(v as ViewMode); setPage(1); }}
              data={[
                { value: 'due-week', label: t('accounting.cheques.views.dueWeek') },
                { value: 'pending', label: t('accounting.cheques.views.pending') },
                { value: 'all', label: t('accounting.cheques.views.all') },
              ]}
            />
            <Select
              data={[
                { value: '1', label: t('accounting.cheques.direction.inbound') },
                { value: '2', label: t('accounting.cheques.direction.outbound') },
              ]}
              value={direction}
              onChange={setDirection}
              placeholder={t('accounting.cheques.directionPlaceholder')}
              clearable
              style={{ minWidth: 160 }}
            />
          </Group>
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setCreateOpen(true)}>
            {t('accounting.cheques.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.cheques.columns.number')}</Table.Th>
                <Table.Th>{t('accounting.cheques.columns.direction')}</Table.Th>
                <Table.Th>{t('accounting.cheques.columns.drawer')}</Table.Th>
                <Table.Th>{t('accounting.cheques.columns.bank')}</Table.Th>
                <Table.Th>{t('accounting.cheques.columns.dueDate')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.cheques.columns.amount')}</Table.Th>
                <Table.Th>{t('accounting.cheques.columns.status')}</Table.Th>
                <Table.Th style={{ width: 120 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((c) => (
                <Table.Tr key={c.chequeId}>
                  <Table.Td className="font-numeric">{c.chequeNumber}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" radius="sm"
                      color={c.direction === 1 ? 'green' : 'orange'}>
                      {c.direction === 1
                        ? t('accounting.cheques.direction.inbound')
                        : t('accounting.cheques.direction.outbound')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{c.drawerName}</Table.Td>
                  <Table.Td>{c.bankNameAr}</Table.Td>
                  <Table.Td className="font-numeric">{c.dueDate}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {c.amount.toFixed(2)}{c.currencyCode !== 'ILS' ? ` ${c.currencyCode}` : ''}
                  </Table.Td>
                  <Table.Td><ChequeStatusBadge status={c.status} /></Table.Td>
                  <Table.Td>
                    <TransitionMenu
                      cheque={c}
                      onTransition={(status) =>
                        changeStatus.mutate({ id: c.chequeId, status })}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.cheques.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data && data.total > 50 && (
            <Group justify="center" p="md">
              <Pagination total={Math.ceil(data.total / 50)} value={page} onChange={setPage} />
            </Group>
          )}
        </Paper>
      )}

      <CreateChequeDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: QK })}
      />
    </Stack>
  );
}

// Mirrors ChequeService.Transitions.
const TRANSITIONS: Record<number, number[]> = {
  1: [2, 3, 4, 6],
  2: [3, 4],
  4: [5],
  3: [], 5: [], 6: [],
};

function TransitionMenu({
  cheque, onTransition,
}: { cheque: ChequeDto; onTransition: (status: number) => void }) {
  const { t } = useTranslation();
  const targets = TRANSITIONS[cheque.status] ?? [];
  if (targets.length === 0) return null;
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button size="compact-sm" variant="light"
          rightSection={<IconChevronDown size={14} stroke={1.6} />}>
          {t('accounting.cheques.changeStatus')}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {targets.map((s) => (
          <Menu.Item key={s} onClick={() => onTransition(s)}>
            {t(statusKey(s))}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function ChequeStatusBadge({ status }: { status: number }) {
  const { t } = useTranslation();
  const colors: Record<number, string> = {
    1: 'yellow', 2: 'blue', 3: 'green', 4: 'red', 5: 'gray', 6: 'gray',
  };
  return (
    <Badge size="xs" variant="light" color={colors[status] ?? 'gray'} radius="sm">
      {t(statusKey(status))}
    </Badge>
  );
}

function statusKey(status: number): string {
  switch (status) {
    case 1: return 'accounting.cheques.status.pending';
    case 2: return 'accounting.cheques.status.deposited';
    case 3: return 'accounting.cheques.status.cleared';
    case 4: return 'accounting.cheques.status.bounced';
    case 5: return 'accounting.cheques.status.returned';
    case 6: return 'accounting.cheques.status.cancelled';
    default: return 'accounting.cheques.status.pending';
  }
}

function CreateChequeDrawer({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const [direction, setDirection] = useState<string>('1');
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [drawerName, setDrawerName] = useState('');
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('ILS');
  const [issueDate, setIssueDate] = useState<Date | null>(new Date());
  const [dueDate, setDueDate] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/accounting/cheques', {
          direction: Number(direction),
          chequeNumber: chequeNumber.trim(),
          bankNameAr: bankName.trim(),
          drawerName: drawerName.trim(),
          amount,
          currencyCode: currency,
          issueDate: toIsoDate(issueDate!),
          dueDate: toIsoDate(dueDate!),
          relatedCustomerId: null,
          relatedSupplierId: null,
          notes: notes.trim() || null,
        })
      ).data,
  });

  const valid =
    chequeNumber.trim() !== '' && bankName.trim() !== '' && drawerName.trim() !== '' &&
    amount > 0 && issueDate !== null && dueDate !== null;

  const submit = async () => {
    try {
      await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: chequeNumber });
      onCreated();
      onClose();
      setChequeNumber(''); setBankName(''); setDrawerName(''); setAmount(0); setNotes('');
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('accounting.cheques.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="md"
      padding="lg"
    >
      <Stack gap="md">
        <SegmentedControl
          value={direction}
          onChange={setDirection}
          data={[
            { value: '1', label: t('accounting.cheques.direction.inbound') },
            { value: '2', label: t('accounting.cheques.direction.outbound') },
          ]}
        />
        <Group grow>
          <TextInput
            label={t('accounting.cheques.form.numberLabel')}
            value={chequeNumber}
            onChange={(e) => setChequeNumber(e.currentTarget.value)}
            withAsterisk
            className="font-numeric"
          />
          <TextInput
            label={t('accounting.cheques.form.bankLabel')}
            value={bankName}
            onChange={(e) => setBankName(e.currentTarget.value)}
            withAsterisk
          />
        </Group>
        <TextInput
          label={t('accounting.cheques.form.drawerLabel')}
          description={t('accounting.cheques.form.drawerHelp')}
          value={drawerName}
          onChange={(e) => setDrawerName(e.currentTarget.value)}
          withAsterisk
        />
        <Group grow>
          <NumberInput
            label={t('accounting.cheques.form.amountLabel')}
            value={amount}
            onChange={(v) => setAmount(Number(v) || 0)}
            min={0}
            decimalScale={2}
            hideControls
          />
          <Select
            label={t('accounting.cheques.form.currencyLabel')}
            data={['ILS', 'JOD', 'USD']}
            value={currency}
            onChange={(v) => setCurrency(v ?? 'ILS')}
          />
        </Group>
        <Group grow>
          <DatePickerInput
            label={t('accounting.cheques.form.issueDateLabel')}
            value={issueDate}
            onChange={setIssueDate}
          />
          <DatePickerInput
            label={t('accounting.cheques.form.dueDateLabel')}
            description={t('accounting.cheques.form.dueDateHelp')}
            value={dueDate}
            onChange={setDueDate}
          />
        </Group>
        <Textarea
          label={t('accounting.cheques.form.notesLabel')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          rows={2}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
